/**
 * The printable **Pull list** for a batch — the ONE page the picker carries to the shelf: the blanks
 * to gather before the film prints, collapsed by physical blank (colour + size), a tick box per row.
 * It replaces the five-page run sheet (`docs/pull-list-plan-v1.md`): the press half of that document
 * is the scan station's job now, and the picker only ever wanted this table.
 *
 * Deliberately ink-on-white (a paper document): explicit light colours regardless of the app theme,
 * every state in weight / rules / outlined boxes — never colour — so nothing is lost on a B&W laser.
 * Rendered inside the PullList overlay's `#pull-list-print` portal, which `globals.css` isolates on
 * print and forces to solid black.
 *
 * What is NOT on it, on purpose: a cancelled line, a refunded copy and a transfer-only reprint are left
 * out by `blanksToPull`. The last one matters most — mark-printed draws no replacement blank for it, so
 * listing it would walk a shirt off the shelf that the ledger never decrements.
 *
 * Pulled-blanks v2 (D18): the same table is the PULL STATION on screen. The overlay hands in `got` — the
 * live *Got* cell per row (a tap records the row) — and `after` — an editor row under the one being
 * edited. Both are screen-only; the paper keeps its pen tick box and its *Pulled by* line, and gains the
 * footer sentence that says the ticks on the screen are the record.
 */
import type { BlankOnHand } from '@fsp/contracts';
import type { ReactNode } from 'react';
import {
  type BatchManifest,
  type BlankPull,
  blanksToPull,
  runSheetOrders,
  type ScreenPrintWorkItem,
} from './batch-lib';

/** Outlined checkbox — ticked by pen on the printed page. Inherits the current (ink) colour. */
function Tick() {
  return (
    <span className="inline-block size-[13px] shrink-0 rounded-[2px] border-[1.25px] border-current align-[-2px]" />
  );
}

export function PullListSheet({
  label,
  manifest,
  screenPrintWork,
  nameBySku,
  got,
  after,
}: {
  label: string;
  manifest: BatchManifest;
  /** The batch's screen-print placements (off the sheet). ⚠️ A filmless run has ZERO transfers, so
   *  without these the page would tell the picker to pull nothing for a run of 40 shirts. */
  screenPrintWork: readonly ScreenPrintWorkItem[];
  nameBySku: ReadonlyMap<string, BlankOnHand>;
  /** The pull station's live *Got* cell for a row (screen only; the paper keeps its tick box). */
  got?: (row: BlankPull) => ReactNode;
  /** An editor rendered as a full-width row under this row (screen only); `null` for none. */
  after?: (row: BlankPull) => ReactNode;
}) {
  const m = manifest;
  const pulls = blanksToPull(m, nameBySku, screenPrintWork);
  const pullTotal = pulls.reduce((s, p) => s + p.qty, 0);
  const orderCount = runSheetOrders(m, screenPrintWork).length;
  const printedOn = new Date().toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <div className="mx-auto max-w-[816px] bg-white p-1 text-[12px] leading-[1.4] text-[#1a1714]">
      {/* ── masthead ── */}
      <div className="flex items-center gap-4">
        <img src="/fsp-logo.png" alt="Fast Screen Print" className="h-[52px] w-auto" />
        <div className="min-w-0 flex-1">
          <div className="text-[9.5px] font-bold uppercase tracking-[0.18em] text-[#625d57]">
            Gang sheet · Production control
          </div>
          <div className="text-[25px] font-extrabold uppercase leading-none tracking-[0.015em]">
            Pull list
          </div>
          <div className="text-[11.5px] text-[#625d57]">
            Gather every blank below before the film prints
          </div>
        </div>
        <div className="whitespace-nowrap text-right font-mono text-[10px] leading-[1.5] text-[#625d57]">
          <span className="block text-[14px] text-[#1a1714]">{label}</span>
          {m.brandLabel ?? 'Fast Screen Print'}
          <br />
          Printed {printedOn}
        </div>
      </div>
      <div className="mt-3 border-t-[2.5px] border-[#141210]" />

      {/* ── counts ── */}
      <div className="mt-4 grid grid-cols-3 overflow-hidden rounded-[5px] border border-[#141210]">
        {[
          { lbl: 'Blanks to pull', val: pullTotal },
          { lbl: 'Orders', val: orderCount },
          { lbl: 'Sheets', val: m.sheetCount },
        ].map((c, i) => (
          <div key={c.lbl} className={i < 2 ? 'border-r border-[#141210] px-3 py-2' : 'px-3 py-2'}>
            <div className="text-[8.5px] font-bold uppercase tracking-[0.11em] text-[#625d57]">
              {c.lbl}
            </div>
            <div className="mt-0.5 font-mono text-[18px] font-semibold tabular-nums">{c.val}</div>
          </div>
        ))}
      </div>

      {/* ── the list ── */}
      <div className="mt-4 mb-1.5 flex items-baseline justify-between gap-2">
        <h2 className="text-[11px] font-extrabold uppercase tracking-[0.12em]">Blanks to pull</h2>
        <span className="text-[9.5px] text-[#625d57]">tick each row as it comes off the shelf</span>
      </div>
      {pulls.length === 0 ? (
        <p className="rounded-[5px] border border-[#141210] px-3 py-2 text-[11px]">
          Nothing to pull — every garment on this batch is cancelled, refunded, or a transfer-only
          reprint that presses onto a shirt already on the floor.
        </p>
      ) : (
        <div className="rounded-[5px] border border-[#141210]">
          <table className="w-full border-collapse">
            <colgroup>
              {/* The live Got cell needs room for "got 20 of 24 · 4 short"; the paper's tick box does not. */}
              <col className={got ? 'w-[6.5rem] sm:w-[7.75rem] print:w-[34px]' : 'w-[34px]'} />
              <col />
              <col className={got ? 'w-[26%] sm:w-[34%] print:w-[34%]' : 'w-[34%]'} />
              <col className={got ? 'w-[40px] sm:w-[54px] print:w-[54px]' : 'w-[54px]'} />
            </colgroup>
            <thead>
              <tr className="[&>th]:border-b [&>th]:border-[#141210] [&>th]:px-3 [&>th]:py-1.5 [&>th]:text-[8.5px] [&>th]:font-bold [&>th]:uppercase [&>th]:tracking-[0.09em] [&>th]:text-[#625d57]">
                <th className="text-center">Got</th>
                <th className="text-left">Blank</th>
                <th className="text-left">Colour · size</th>
                <th className="text-right">Qty</th>
              </tr>
            </thead>
            <tbody>
              {pulls.map((p) => {
                const editor = after?.(p);
                return (
                  <PullRowGroup key={p.key}>
                    <tr
                      aria-label={`${p.label} ${p.variant || p.sizeBand}`}
                      className="[&>td]:border-b [&>td]:border-[#e2ded7] [&>td]:px-3 [&>td]:py-1.5"
                    >
                      <td className="text-center align-middle">
                        {got ? (
                          <>
                            <span className="print:hidden">{got(p)}</span>
                            <span className="hidden print:inline-block">
                              <Tick />
                            </span>
                          </>
                        ) : (
                          <Tick />
                        )}
                      </td>
                      <td className="align-middle font-semibold">{p.label}</td>
                      <td className="align-middle text-[#625d57]">{p.variant || p.sizeBand}</td>
                      <td className="text-right align-middle font-mono text-[13px] font-semibold tabular-nums">
                        {p.qty}
                      </td>
                    </tr>
                    {editor ? (
                      <tr className="print:hidden">
                        <td
                          colSpan={4}
                          className="border-b border-[#e2ded7] bg-[#f6f4f0] px-3 py-2"
                        >
                          {editor}
                        </td>
                      </tr>
                    ) : null}
                  </PullRowGroup>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── footer ── */}
      <div className="mt-5 border-t border-[#e2ded7] pt-2 font-mono text-[9.5px] text-[#625d57]">
        <div className="flex items-end justify-between gap-4">
          <span>Fast Screen Print · Pull list · {label}</span>
          <span className="text-[#1a1714]">
            Pulled by <span className="mx-1 inline-block w-[120px] border-b border-[#141210]" />
          </span>
        </div>
        {/* D18 — the paper is a walking aid; the ticks on the screen are the record. */}
        <p className="mt-1 text-[#1a1714]">
          When the stack is sorted, tap Pulled on the screen — these blanks stay in the book until
          pressed.
        </p>
      </div>
    </div>
  );
}

/** A row and its optional editor row share one key; a fragment keeps the table's children flat. */
function PullRowGroup({ children }: { children: ReactNode }) {
  return <>{children}</>;
}
